import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  getRevenueAggregate,
  getRevenuePayments,
  getSubscriptionAudit,
  invoicePdfUrl,
  type AdminRevenueAggregateResponse,
  type AdminRevenueFilters,
  type AdminRevenueGroupBy,
  type AdminRevenuePayment,
  type AdminSubscriptionAuditItem,
} from "@admin/lib/api";
import { formatCents, formatDate } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";
import { Input } from "@admin/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import {
  CategoryDonut,
  DailyStackedChart,
  type SeriesDef,
} from "@admin/components/charts/SpendCharts";

const SELECT_CLASS =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg focus:outline-none focus:ring-1 focus:ring-accent";

const GROUP_BY: AdminRevenueGroupBy[] = ["day", "type", "plan", "country", "user"];

const DAILY_SERIES: SeriesDef[] = [
  { key: "subscription", label: "Subscription", color: "#818cf8" },
  { key: "topup", label: "Top-up", color: "#34d399" },
];

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-fg">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint ? <p className="mt-1 text-xs text-muted-fg">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

function statusPill(status: string) {
  const paid = status === "paid";
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs ${
        paid ? "bg-emerald-500/20 text-emerald-300" : "bg-danger/20 text-danger"
      }`}
    >
      {status}
    </span>
  );
}

type Tab = "payments" | "subscriptions" | "plan-changes";

export default function RevenuePage() {
  const [filters, setFilters] = useState<AdminRevenueFilters>({
    status: "paid",
    hasVat: "all",
    groupBy: "type",
  });
  const [data, setData] = useState<AdminRevenueAggregateResponse | null>(null);
  const [payments, setPayments] = useState<AdminRevenuePayment[]>([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsOffset, setPaymentsOffset] = useState(0);
  const [audit, setAudit] = useState<AdminSubscriptionAuditItem[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditOffset, setAuditOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("payments");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const limit = 50;

  const load = async (f: AdminRevenueFilters, payOff = 0, auditOff = 0) => {
    setLoading(true);
    setErr(null);
    try {
      const [agg, pay, aud] = await Promise.all([
        getRevenueAggregate(f),
        getRevenuePayments({ ...f, limit, offset: payOff }),
        getSubscriptionAudit({
          from: f.from,
          to: f.to,
          plan: f.plan,
          limit,
          offset: auditOff,
        }),
      ]);
      setData(agg);
      setPayments(pay.items);
      setPaymentsTotal(pay.total);
      setPaymentsOffset(payOff);
      setAudit(aud.items);
      setAuditTotal(aud.total);
      setAuditOffset(auditOff);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load revenue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof AdminRevenueFilters>(key: K, value: AdminRevenueFilters[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const dailyData = useMemo(
    () =>
      (data?.daily ?? []).map((d) => ({
        day: d.day,
        subscription: d.subscriptionCents,
        topup: d.topupCents,
      })),
    [data],
  );

  const mixDonut = useMemo(() => {
    if (!data) return [];
    return [
      {
        key: "subscription",
        label: "Subscription",
        value: data.totals.subscriptionCents,
        color: "#818cf8",
      },
      {
        key: "topup",
        label: "Top-up",
        value: data.totals.topupCents,
        color: "#34d399",
      },
    ];
  }, [data]);

  const planDonut = useMemo(() => {
    if (!data) return [];
    const colors: Record<string, string> = {
      solo: "#818cf8",
      pro: "#34d399",
      team: "#fbbf24",
    };
    return Object.entries(data.totals.byPlan).map(([key, value]) => ({
      key,
      label: key,
      value: value * 100,
      color: colors[key] ?? "#94a3b8",
    }));
  }, [data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Revenue</h1>
        <p className="mt-1 text-sm text-muted-fg">
          Cash collected from subscriptions and top-ups (not usage COGS - see Spend).
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          From
          <Input
            type="date"
            value={filters.from ?? ""}
            onChange={(e) => set("from", e.target.value || undefined)}
            className="h-9"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          To
          <Input
            type="date"
            value={filters.to ?? ""}
            onChange={(e) => set("to", e.target.value || undefined)}
            className="h-9"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Type
          <select
            className={SELECT_CLASS}
            value={filters.type ?? ""}
            onChange={(e) =>
              set("type", (e.target.value || undefined) as AdminRevenueFilters["type"])
            }
          >
            <option value="">All</option>
            <option value="subscription">Subscription</option>
            <option value="topup">Top-up</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Status
          <select
            className={SELECT_CLASS}
            value={filters.status ?? "paid"}
            onChange={(e) => set("status", e.target.value as AdminRevenueFilters["status"])}
          >
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="all">All</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Plan
          <select
            className={SELECT_CLASS}
            value={filters.plan ?? ""}
            onChange={(e) =>
              set("plan", (e.target.value || undefined) as AdminRevenueFilters["plan"])
            }
          >
            <option value="">All</option>
            <option value="solo">Solo</option>
            <option value="pro">Pro</option>
            <option value="team">Team</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Country
          <Input
            value={filters.country ?? ""}
            onChange={(e) => set("country", e.target.value || undefined)}
            placeholder="e.g. DE"
            className="h-9 w-24"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Has VAT ID
          <select
            className={SELECT_CLASS}
            value={filters.hasVat ?? "all"}
            onChange={(e) => set("hasVat", e.target.value as AdminRevenueFilters["hasVat"])}
          >
            <option value="all">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-fg">
          Group by
          <select
            className={SELECT_CLASS}
            value={filters.groupBy ?? "type"}
            onChange={(e) => set("groupBy", e.target.value as AdminRevenueGroupBy)}
          >
            {GROUP_BY.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
        <Button size="sm" onClick={() => void load(filters)} disabled={loading}>
          {loading ? "Loading..." : "Apply"}
        </Button>
      </div>

      {err && <p className="text-danger">{err}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Collected" value={formatCents(data?.totals.collectedCents ?? 0)} />
        <KpiCard
          label="Subscription"
          value={formatCents(data?.totals.subscriptionCents ?? 0)}
        />
        <KpiCard label="Top-ups" value={formatCents(data?.totals.topupCents ?? 0)} />
        <KpiCard
          label="MRR today"
          value={formatCents(data?.totals.mrrCents ?? 0)}
          hint="Active-like run-rate"
        />
        <KpiCard
          label="Active subscribers"
          value={String(data?.totals.activeSubscriberCount ?? 0)}
          hint={
            data
              ? `solo ${data.totals.byPlan.solo ?? 0} / pro ${data.totals.byPlan.pro ?? 0} / team ${data.totals.byPlan.team ?? 0}`
              : undefined
          }
        />
        <KpiCard label="Failed payments" value={String(data?.totals.failedCount ?? 0)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Daily revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <DailyStackedChart
              data={dailyData}
              series={DAILY_SERIES}
              formatValue={formatCents}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revenue mix</CardTitle>
          </CardHeader>
          <CardContent>
            <CategoryDonut data={mixDonut} formatValue={formatCents} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active subscribers by plan</CardTitle>
        </CardHeader>
        <CardContent>
          <CategoryDonut
            data={planDonut}
            formatValue={(v) => String(Math.round(v / 100))}
          />
        </CardContent>
      </Card>

      <div className="flex gap-2 border-b border-border pb-2">
        {(
          [
            ["payments", "Payments"],
            ["subscriptions", "Subscriptions"],
            ["plan-changes", "Plan changes"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={
              tab === id
                ? "rounded-md bg-accent/20 px-3 py-1.5 text-sm text-fg"
                : "rounded-md px-3 py-1.5 text-sm text-muted-fg hover:text-fg"
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "payments" && (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/50 text-muted-fg">
                <tr>
                  <th className="px-3 py-3 font-medium" />
                  <th className="px-3 py-3 font-medium">Date</th>
                  <th className="px-3 py-3 font-medium">User</th>
                  <th className="px-3 py-3 font-medium">Type</th>
                  <th className="px-3 py-3 font-medium">Amount</th>
                  <th className="px-3 py-3 font-medium">Tax</th>
                  <th className="px-3 py-3 font-medium">Plan</th>
                  <th className="px-3 py-3 font-medium">Billing name</th>
                  <th className="px-3 py-3 font-medium">Country</th>
                  <th className="px-3 py-3 font-medium">VAT</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <Fragment key={p.id}>
                    <tr className="border-b border-border last:border-0">
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-muted-fg hover:text-fg"
                          onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                          aria-label="Expand payment detail"
                        >
                          {expanded === p.id ? "v" : ">"}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-muted-fg">{formatDate(p.createdAt)}</td>
                      <td className="px-3 py-2">
                        <Link to={`/users/${p.userId}`} className="text-accent hover:underline">
                          {p.email}
                        </Link>
                      </td>
                      <td className="px-3 py-2">{p.type}</td>
                      <td className="px-3 py-2 tabular-nums">{formatCents(p.amountUsdCents)}</td>
                      <td className="px-3 py-2 tabular-nums">
                        {p.taxAmountCents != null ? formatCents(p.taxAmountCents) : "-"}
                      </td>
                      <td className="px-3 py-2">{p.planCode ?? "-"}</td>
                      <td className="px-3 py-2">{p.billingName ?? "-"}</td>
                      <td className="px-3 py-2">{p.billingCountry ?? "-"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{p.billingVatId ?? "-"}</td>
                      <td className="px-3 py-2">{statusPill(p.status)}</td>
                      <td className="px-3 py-2">
                        {p.hasInvoicePdf ? (
                          <a
                            href={invoicePdfUrl(p.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-accent hover:underline"
                          >
                            PDF
                          </a>
                        ) : (
                          <span className="text-muted-fg" title="No invoice PDF">
                            -
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded === p.id ? (
                      <tr className="border-b border-border bg-muted/20">
                        <td colSpan={12} className="px-4 py-3 text-xs text-muted-fg">
                          <div className="grid gap-1 sm:grid-cols-2">
                            <div>Reference: {p.externalId}</div>
                            <div>Stripe customer: {p.stripeCustomerId ?? "-"}</div>
                            <div>
                              Subtotal:{" "}
                              {p.subtotalCents != null ? formatCents(p.subtotalCents) : "-"}
                            </div>
                            <div>
                              Total: {p.totalCents != null ? formatCents(p.totalCents) : "-"}
                            </div>
                            <div>Tax ID type: {p.billingVatIdType ?? "-"}</div>
                            <div>Tax exempt: {p.customerTaxExempt ?? "-"}</div>
                            <div className="sm:col-span-2 break-all">
                              Address:{" "}
                              {p.billingAddress ? JSON.stringify(p.billingAddress) : "-"}
                            </div>
                            {p.stripeCustomerId ? (
                              <div>
                                <a
                                  href={`https://dashboard.stripe.com/customers/${p.stripeCustomerId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-accent hover:underline"
                                >
                                  Open in Stripe
                                </a>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
                {payments.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-4 py-6 text-center text-muted-fg">
                      No payments match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={paymentsOffset === 0}
              onClick={() => void load(filters, Math.max(0, paymentsOffset - limit), auditOffset)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={paymentsOffset + limit >= paymentsTotal}
              onClick={() => void load(filters, paymentsOffset + limit, auditOffset)}
            >
              Next
            </Button>
            <span className="self-center text-xs text-muted-fg">
              {paymentsTotal} payments
            </span>
          </div>
        </div>
      )}

      {tab === "subscriptions" && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/50 text-muted-fg">
              <tr>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Period end</th>
                <th className="px-4 py-3 font-medium">Cancel at end</th>
                <th className="px-4 py-3 font-medium">Scheduled</th>
                <th className="px-4 py-3 font-medium">Lifetime paid</th>
              </tr>
            </thead>
            <tbody>
              {(data?.subscriptionSnapshot ?? []).map((s) => (
                <tr key={s.userId} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <Link to={`/users/${s.userId}`} className="text-accent hover:underline">
                      {s.email}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{s.planCode}</td>
                  <td className="px-4 py-3">{s.status}</td>
                  <td className="px-4 py-3 text-muted-fg">{formatDate(s.currentPeriodEnd)}</td>
                  <td className="px-4 py-3">{s.cancelAtPeriodEnd ? "yes" : "no"}</td>
                  <td className="px-4 py-3">{s.scheduledPlanCode ?? "-"}</td>
                  <td className="px-4 py-3 tabular-nums">{formatCents(s.lifetimePaidCents)}</td>
                </tr>
              ))}
              {data && data.subscriptionSnapshot.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-fg">
                    No subscriptions.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "plan-changes" && (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/50 text-muted-fg">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">From plan</th>
                  <th className="px-4 py-3 font-medium">To plan</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Effective</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 text-muted-fg">{formatDate(a.createdAt)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/users/${a.userId}`} className="text-accent hover:underline">
                        {a.email}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{a.eventType}</td>
                    <td className="px-4 py-3">{a.fromPlanCode ?? "-"}</td>
                    <td className="px-4 py-3">{a.toPlanCode ?? "-"}</td>
                    <td className="px-4 py-3 text-xs">
                      {a.fromStatus ?? "-"} → {a.toStatus ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-muted-fg">
                      {a.effectiveAt ? formatDate(a.effectiveAt) : "-"}
                    </td>
                  </tr>
                ))}
                {audit.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-muted-fg">
                      No plan changes in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={auditOffset === 0}
              onClick={() => void load(filters, paymentsOffset, Math.max(0, auditOffset - limit))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={auditOffset + limit >= auditTotal}
              onClick={() => void load(filters, paymentsOffset, auditOffset + limit)}
            >
              Next
            </Button>
            <span className="self-center text-xs text-muted-fg">{auditTotal} events</span>
          </div>
        </div>
      )}

      {data && data.groups.length > 0 && tab === "payments" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Grouped ({data.groupBy})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-left text-sm">
              <thead className="border-y border-border bg-muted/50 text-muted-fg">
                <tr>
                  <th className="px-4 py-3 font-medium">Group</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Count</th>
                </tr>
              </thead>
              <tbody>
                {data.groups.map((g) => (
                  <tr key={g.key} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      {data.groupBy === "user" ? (
                        <Link to={`/users/${g.key}`} className="text-accent hover:underline">
                          {g.label ?? g.key}
                        </Link>
                      ) : (
                        g.label ?? g.key
                      )}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{formatCents(g.amountUsdCents)}</td>
                    <td className="px-4 py-3 tabular-nums">{g.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
